import React, { useEffect } from 'react'
import { Box, Typography, Grid, Card, CardContent, IconButton, Chip, useTheme, useMediaQuery } from '@mui/material'
import { Refresh as RefreshIcon } from '@mui/icons-material'

import { useAuth } from '../contexts/AuthContext'
import MetricsCards from '../components/dashboard/MetricsCards'
import FunnelChart from '../components/dashboard/FunnelChart'
import LeadSourcesChart from '../components/dashboard/LeadSourcesChart'
import RecentLeadsTable from '../components/dashboard/RecentLeadsTable'
import QuickActions from '../components/dashboard/QuickActions'
import ChannelStatus from '../components/dashboard/ChannelStatus'
import SMBList from '../components/dashboard/SMBList'
import { useNotification } from '../contexts/NotificationContext'
import PageBanner from '../components/PageBanner'

const AgencyDashboard: React.FC = () => {
  const { user } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { showNotification } = useNotification()

  const handleRefresh = () => {
    showNotification('Dashboard refreshed', 'info')
    // In a real app, this would trigger data refetch
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const status = params.get('status')
    
    if (status) {
      if (status === 'connected') {
        const domain = params.get('store_domain') || 'Store'
        showNotification(`${domain} connected! Fetching Products...`, 'success')
        
        // Mark this domain as actively fetching in this session so the Drawer can show the success toast later
        if (domain !== 'Store') {
          sessionStorage.setItem(`shopify_sync_pending_${domain}`, 'true')
        }
      } else if (status === 'failed') {
        showNotification('Failed to connect Shopify store. Please try again.', 'error')
      }
      
      // Clean up URL to prevent toast on refresh
      const newUrl = window.location.pathname
      window.history.replaceState({}, '', newUrl)
    }
  }, [showNotification])

  // Render different dashboards based on user role
  if (user?.role === 'agency') {
    return (
      <Box>
        {/* Agency Dashboard Header */}
        <PageBanner
          title="Agency Dashboard"
          subtitle={isMobile ? 'Manage client businesses' : 'Manage your client businesses and growth strategies'}
          onRefresh={handleRefresh}
        />

        {/* Agency Metrics */}
        <MetricsCards />

        {/* SMB Management - Primary feature for agencies */}
        <Box sx={{ mb: 3 }}>
          <SMBList />
        </Box>

        {/* Agency Analytics */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={6}>
            <FunnelChart />
          </Grid>
          <Grid item xs={12} lg={6}>
            <LeadSourcesChart />
          </Grid>
        </Grid>

        {/* Recent Activity & Quick Actions */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <RecentLeadsTable />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <QuickActions />
              <ChannelStatus />
            </Box>
          </Grid>
        </Grid>
      </Box>
    )
  }

  // SMB Dashboard (default)
  return (
    <Box>
      {/* SMB Dashboard Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 2 }}>
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                Sunset Resort & Spa
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Hospitality Dashboard - Drive bookings
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                label="Active" 
                sx={{ 
                  bgcolor: 'rgba(76, 175, 80, 0.2)', 
                  color: '#4caf50',
                  fontWeight: 'bold'
                }} 
              />
              <IconButton
                onClick={handleRefresh}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.1)', 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* SMB Metrics */}
      <MetricsCards />

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <FunnelChart />
        </Grid>
        <Grid item xs={12} lg={6}>
          <LeadSourcesChart />
        </Grid>
      </Grid>

      {/* Recent Activity & Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <RecentLeadsTable />
        </Grid>
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <QuickActions />
            <ChannelStatus />
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default AgencyDashboard
